import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { adminAPI } from '../api/api';
import { CreditCard, Euro, Building2, ExternalLink, FileText, RotateCcw, XCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Card, StatTile, Btn, PlanBadge, StatusDot, formatDate, formatDateTime, formatEuros, Spinner } from '../components/admin/AdminUI';

const STRIPE_DASHBOARD_BASE = 'https://dashboard.stripe.com';

const AdminPayments = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [overview, setOverview] = useState(null);
  const [subs, setSubs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { document.title = "Skilldeck — Admin · Paiements"; }, []);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [o, s, i] = await Promise.all([
        adminAPI.billingOverview(),
        adminAPI.listSubscriptions(),
        adminAPI.recentInvoices(25).catch(() => []),
      ]);
      setOverview(o); setSubs(s); setInvoices(i);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger les données de paiement', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const cancelSub = async (orgId, orgName) => {
    if (!window.confirm(`Annuler l'abonnement de "${orgName}" immédiatement ?`)) return;
    try {
      await adminAPI.cancelSubscription(orgId);
      toast({ title: 'Abonnement annulé' });
      fetchAll();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
  };

  const refund = async (invoiceId, amount) => {
    if (!window.confirm(`Rembourser ${formatEuros(amount)} ? Cette action est irréversible.`)) return;
    try {
      const r = await adminAPI.refundInvoice(invoiceId);
      toast({ title: 'Remboursement effectué', description: `${formatEuros(r.amount_eur)} — ${r.status}` });
      fetchAll();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
  };

  const filteredSubs = statusFilter === 'all' ? subs : subs.filter((s) => s.status === statusFilter);

  if (loading) {
    return (
      <AdminLayout title="Paiements">
        <div className="flex justify-center py-24"><Spinner /></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Paiements">
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sd-stagger">
          <StatTile icon={Euro} label="MRR" value={overview?.mrr_eur || 0} formatter={formatEuros} accent="emerald" />
          <StatTile icon={Euro} label="ARR" value={overview?.arr_eur || 0} formatter={formatEuros} accent="violet" />
          <StatTile icon={CreditCard} label="Abonnements actifs"
            value={(overview?.subscriptions_by_status?.active || 0) + (overview?.subscriptions_by_status?.trialing || 0)}
            accent="blue" />
          <StatTile icon={XCircle} label="Impayés" value={overview?.subscriptions_by_status?.past_due || 0} accent="amber" />
        </div>

        {/* Subscriptions */}
        <Card padding="p-0">
          <div className="px-5 py-4 border-b border-[rgba(108,71,255,.12)] flex items-center gap-3">
            <CreditCard className="h-4 w-4 text-violet-300" />
            <h3 className="text-sm font-semibold text-white">Abonnements ({filteredSubs.length})</h3>
            <div className="ml-auto">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-xs text-white">
                <option value="all">Tous</option>
                <option value="active">Actifs</option>
                <option value="trialing">Essai</option>
                <option value="past_due">Impayés</option>
                <option value="canceled">Annulés</option>
              </select>
            </div>
          </div>

          {filteredSubs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">Aucun abonnement</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(108,71,255,.1)] text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    <th className="px-5 py-3">Organisation</th>
                    <th className="px-5 py-3">Plan</th>
                    <th className="px-5 py-3">Statut</th>
                    <th className="px-5 py-3">Période en cours</th>
                    <th className="px-5 py-3">Renouvellement</th>
                    <th className="px-5 py-3">Stripe</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(108,71,255,.06)]">
                  {filteredSubs.map((s) => {
                    const orgName = s.organizations?.name || s.organization_id;
                    return (
                      <tr key={s.id} className="hover:bg-[rgba(108,71,255,.04)] transition-colors">
                        <td className="px-5 py-3">
                          <button onClick={() => navigate(`/admin/orgs/${s.organization_id}`)}
                            className="text-sm text-white hover:text-violet-300 inline-flex items-center gap-1.5">
                            <Building2 className="h-3.5 w-3.5 text-gray-500" /> {orgName}
                          </button>
                          <p className="text-[11px] text-gray-500 font-mono">{s.organizations?.slug}</p>
                        </td>
                        <td className="px-5 py-3"><PlanBadge code={s.plans?.code} name={s.plans?.name} /></td>
                        <td className="px-5 py-3"><StatusDot status={s.status} /></td>
                        <td className="px-5 py-3 text-xs text-gray-400">{formatDate(s.current_period_start)}</td>
                        <td className="px-5 py-3 text-xs text-gray-400">
                          {formatDate(s.current_period_end)}
                          {s.cancel_at_period_end && <p className="text-[10px] text-amber-400 mt-0.5">Annulation à la fin</p>}
                        </td>
                        <td className="px-5 py-3">
                          {s.stripe_subscription_id ? (
                            <a href={`${STRIPE_DASHBOARD_BASE}/subscriptions/${s.stripe_subscription_id}`}
                              target="_blank" rel="noreferrer"
                              className="text-xs text-violet-300 hover:text-violet-200 inline-flex items-center gap-1">
                              <ExternalLink className="h-3 w-3" /> Ouvrir
                            </a>
                          ) : <span className="text-xs text-gray-600">—</span>}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {['active', 'trialing', 'past_due'].includes(s.status) && (
                            <Btn tone="danger" onClick={() => cancelSub(s.organization_id, orgName)}>
                              <XCircle className="h-3 w-3" /> Annuler
                            </Btn>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Invoices */}
        <Card padding="p-0">
          <div className="px-5 py-4 border-b border-[rgba(108,71,255,.12)] flex items-center gap-2">
            <FileText className="h-4 w-4 text-violet-300" />
            <h3 className="text-sm font-semibold text-white">Factures récentes</h3>
          </div>
          {invoices.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">Aucune facture à afficher</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(108,71,255,.1)] text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Organisation</th>
                    <th className="px-5 py-3">Montant</th>
                    <th className="px-5 py-3">Statut</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(108,71,255,.06)]">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-[rgba(108,71,255,.04)] transition-colors">
                      <td className="px-5 py-3 text-xs text-gray-400">{formatDateTime(inv.created)}</td>
                      <td className="px-5 py-3 text-sm text-gray-200">
                        {inv.org?.name ? (
                          <button onClick={() => navigate(`/admin/orgs/${inv.org.organization_id}`)} className="hover:text-violet-300">{inv.org.name}</button>
                        ) : <span className="text-gray-500 font-mono text-[11px]">{inv.customer}</span>}
                      </td>
                      <td className="px-5 py-3 text-sm font-semibold text-white">{formatEuros(inv.amount_eur)}</td>
                      <td className="px-5 py-3"><StatusDot status={inv.status === 'paid' ? 'active' : inv.status === 'open' ? 'past_due' : inv.status} /></td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          {inv.hosted_invoice_url && (
                            <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer">
                              <Btn tone="subtle"><ExternalLink className="h-3 w-3" /> Voir</Btn>
                            </a>
                          )}
                          {inv.status === 'paid' && (
                            <Btn tone="danger" onClick={() => refund(inv.id, inv.amount_eur)}>
                              <RotateCcw className="h-3 w-3" /> Rembourser
                            </Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminPayments;
